import React, { FC, useCallback, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { Button } from '@ui/components/Button'
import { Chip } from '@ui/components/Chip'
import { ListItem } from '@ui/components/List'
import { Popover, PopoverCard, PopoverList, PopoverSearchInput } from '@ui/components/Popover'
import { ChevronIcon } from '@ui/icons/Chevron.icon'

import { useWindowResize } from '@utils/domUtils'

import { FilterQueryProps } from '@model/FilterFields'
import { NameDto } from '@model/NameDto'

import { Loading } from '@shared/components/Loading/Loading'

import { FilterOptions } from '../TableFilter'

import styles from './FilterChipDropdownList.module.scss'

function isNameDto(x: NameDto<string | number> | string | number): x is NameDto<string | number> {
  return typeof x === 'object' && x !== null && 'id' in x
}

const parseIdsFromState = (state: FilterQueryProps | undefined, key: FilterOptions): string[] => {
  const raw = state?.[key]
  if (raw == null) return []
  return raw
    .toString()
    .split(' ')
    .map((v) => v.trim())
    .filter(Boolean)
}

const stringifyIds = (ids: string[]): string | undefined => (ids.length ? ids.join(' ') : undefined)

interface FilterChipDropdownListProps {
  name: string
  options?: (NameDto<string | number> | string | number)[]
  state?: FilterQueryProps
  filterType: FilterOptions
  isMultiSelect?: boolean
  hasSearch?: boolean
  isLoading?: boolean
  onSelect: (value?: FilterQueryProps) => void
  onSearchChange?: (value: string) => void
  size?: 'sm' | 'md' | 'lg'
}

export const FilterChipDropdownList: FC<FilterChipDropdownListProps> = (props) => {
  const {
    options = [],
    name,
    hasSearch,
    onSelect,
    onSearchChange,
    isMultiSelect,
    filterType,
    state,
    isLoading,
    size = 'md',
  } = props

  const { t } = useTranslation()
  const isMobile = useWindowResize()

  const [searchValue, setSearchValue] = useState('')
  const [isDropdownOpen, setIsDropdownOpen] = useState(false)
  const [tempSelection, setTempSelection] = useState<string[]>([]) // local multi-select buffer

  const sanitizedOptions = useMemo<NameDto<string | number>[]>(() => {
    return options.map((opt) => (isNameDto(opt) ? opt : { id: opt, name: String(opt) }))
  }, [options])

  const filteredOptions = useMemo<NameDto<string | number>[]>(() => {
    if (onSearchChange) {
      return sanitizedOptions
    }
    const lower = searchValue.toLowerCase()
    return sanitizedOptions.filter((opt) => opt.name?.toLowerCase().includes(lower))
  }, [sanitizedOptions, searchValue, onSearchChange])

  const handleSearchChange = useCallback(
    (value: string) => {
      setSearchValue(value)
      onSearchChange?.(value)
    },
    [onSearchChange]
  )

  const handleToggleDropdown = useCallback(
    (dropdownState: boolean) => {
      setIsDropdownOpen(dropdownState)

      if (dropdownState && isMultiSelect) {
        const current = parseIdsFromState(state, filterType)
        setTempSelection(current)
        return
      }

      if (!dropdownState) {
        if (!isMobile && isMultiSelect) {
          const prevIds = parseIdsFromState(state, filterType)
          const prevValue = stringifyIds(prevIds)
          const nextValue = stringifyIds(tempSelection)

          if (prevValue !== nextValue) {
            onSelect({ ...state, [filterType]: nextValue })
          }
        }

        if (searchValue !== '') {
          handleSearchChange('')
        }
      }
    },
    [
      isMobile,
      isMultiSelect,
      state,
      filterType,
      tempSelection,
      onSelect,
      searchValue,
      handleSearchChange,
    ]
  )

  const handleDropdownSelect = (value?: FilterQueryProps) => {
    if (isMultiSelect) return
    onSelect(value)
    handleToggleDropdown(false)
  }

  if (sanitizedOptions.length === 0 && (!hasSearch || !searchValue.trim())) {
    return null
  }

  const activeIds = parseIdsFromState(state, filterType)

  return (
    <Popover
      direction='topLeft'
      isOpen={isDropdownOpen}
      isMultiSelect={isMultiSelect}
      setIsOpen={handleToggleDropdown}
      label={name}
      modalCanSearch={isMobile && !!hasSearch}
      modalInitialSelectedIds={isMobile && isMultiSelect ? activeIds : undefined}
      onApplyOnClose={
        isMobile && isMultiSelect
          ? (ids) => {
              const prevIds = parseIdsFromState(state, filterType)
              const nextIds = ids ?? []
              const sameLength = prevIds.length === nextIds.length
              const prevSet = new Set(prevIds)
              const isSame = sameLength && nextIds.every((id) => prevSet.has(id))

              if (isSame) return
              const value = stringifyIds(nextIds)
              onSelect({ ...state, [filterType]: value })
            }
          : undefined
      }
      onModalSearchChange={isMobile ? handleSearchChange : undefined}
      modalRender={
        isMobile
          ? (ctx) => (
              <PopoverCard>
                <ListContent
                  state={state}
                  filterType={filterType}
                  isMultiSelect={isMultiSelect}
                  filteredOptions={sanitizedOptions.filter((opt) =>
                    opt.name?.toLowerCase().includes(ctx.search.toLowerCase())
                  )}
                  onSelect={handleDropdownSelect}
                  selectedIds={isMultiSelect ? ctx.selectedIds : undefined}
                  onToggle={ctx.toggle}
                  isLoading={isLoading}
                />
              </PopoverCard>
            )
          : undefined
      }
      content={
        <PopoverCard>
          {!isMobile && hasSearch && (
            <PopoverSearchInput
              label={t('Search')}
              value={searchValue}
              onClear={() => handleSearchChange('')}
              onChange={(e) => handleSearchChange(e.target.value)}
            />
          )}
          <ListContent
            state={state}
            filterType={filterType}
            isMultiSelect={isMultiSelect}
            hasSearch={hasSearch}
            filteredOptions={filteredOptions}
            onSelect={handleDropdownSelect}
            selectedIds={isMultiSelect ? tempSelection : undefined}
            onToggle={(id) => {
              if (!isMultiSelect) return
              setTempSelection((prev) =>
                prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
              )
            }}
            isLoading={isLoading}
          />
        </PopoverCard>
      }
    >
      <Button.Secondary
        size={size}
        iconRight={ChevronIcon.Down}
        endDecorator={
          state?.[filterType] && (
            <Chip
              selected
              size='xs'
              onDelete={(event) => {
                event.stopPropagation()
                event.preventDefault()

                onSelect?.({ ...state, [filterType]: undefined })
                setTempSelection([])
                setIsDropdownOpen(false)
                if (searchValue !== '') handleSearchChange('')
              }}
              key={`${filterType}-${state[filterType]}`}
            >
              {activeIds.length.toString() || ''}
            </Chip>
          )
        }
        onClick={() => handleToggleDropdown(!isDropdownOpen)}
      >
        {name}
      </Button.Secondary>
    </Popover>
  )
}

interface ListContentProps {
  filteredOptions?: NameDto<string | number>[]
  state?: FilterQueryProps
  filterType: FilterOptions
  isMultiSelect?: boolean
  hasSearch?: boolean
  onSelect: (value: FilterQueryProps) => void
  isLoading?: boolean
  selectedIds?: string[]
  onToggle?: (id: string) => void
}

export const ListContent: FC<ListContentProps> = (props) => {
  const {
    state,
    filterType,
    isMultiSelect,
    filteredOptions = [],
    onSelect,
    isLoading,
    selectedIds,
    onToggle,
  } = props
  const isMobile = useWindowResize()

  const activeIds = parseIdsFromState(state, filterType)

  const handleItemClick = (option: NameDto<string | number>) => {
    const id = option.id.toString()

    if (isMultiSelect) {
      onToggle?.(id)
      return
    }

    const isAlreadySelected = activeIds.length === 1 && activeIds[0] === id
    const nextValue = isAlreadySelected ? undefined : option.id

    const newState: FilterQueryProps = {
      ...state,
      [filterType]: nextValue,
    }

    onSelect(newState)
  }

  return (
    <PopoverList variant='compact' onMouseOver={(e) => e.stopPropagation()}>
      <Loading isLoading={!!isLoading} showLoadingIcon className={styles.loading}>
        {filteredOptions.map((option) => {
          const id = option.id.toString()
          const isActive = isMultiSelect ? !!selectedIds?.includes(id) : activeIds.includes(id)

          return (
            <ListItem
              key={id}
              size={isMobile ? 'lg' : 'md'}
              endSlot={{ checkbox: true }}
              active={isActive}
              onClick={() => handleItemClick(option)}
            >
              {option.name}
            </ListItem>
          )
        })}
      </Loading>
    </PopoverList>
  )
}
